import React, { useState, useEffect, useRef } from "react";
import { CloseOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import "./ChatBox.css";
import { USER_LOGIN } from "../Utils/Setting/Config";
import { encryptId } from "./SecurityComponent/cryptoUtils";

const AI_API = "/movies-ai-chat.php";
const GREETING = "Chào bạn 👋 Mình là trợ lý phim AI của BCT36. Bạn muốn xem thể loại gì, phim nước nào, hay đang tìm phim cụ thể nào? Cứ mô tả, mình gợi ý ngay!";

const MovieResult = ({ m }) => {
    const [err, setErr] = useState(false);
    const watchUrl = `/watch/${m.slug}/${encryptId(1)}`;
    return (
        <Link to={watchUrl} className="ai-movie-card" title={m.title}>
            <div className="ai-movie-thumb">
                {!err && m.posterUrl
                    ? <img src={m.posterUrl} alt={m.title} loading="lazy" decoding="async" onError={() => setErr(true)} />
                    : <div className="ai-movie-noimg">🎬</div>}
                {m.vip && <span className="ai-movie-vip">VIP</span>}
                {m.averageRating > 0 && <span className="ai-movie-rate">⭐ {Number(m.averageRating).toFixed(1)}</span>}
            </div>
            <div className="ai-movie-title">{m.title}</div>
            <div className="ai-movie-meta">
                {[m.releaseYear, m.episodeCount > 1 ? `${m.episodeCount} tập` : null, m.country]
                    .filter(Boolean).join(" · ")}
            </div>
        </Link>
    );
};

const ChatBox = ({ showChatBox, toggleChatBox }) => {
    const { isAuthenticated } = useSelector((state) => state.UserReducer);
    const [messages, setMessages] = useState([{ role: "ai", content: GREETING, movies: [] }]);
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);
    const [userName, setUserName] = useState(null);
    const contentRef = useRef(null);

    useEffect(() => {
        if (isAuthenticated) {
            const u = JSON.parse(localStorage.getItem(USER_LOGIN) || "null");
            if (u?.username) setUserName(u.username);
        }
    }, [isAuthenticated]);

    const scrollToBottom = () => {
        setTimeout(() => {
            if (contentRef.current) {
                contentRef.current.scrollTo({ top: contentRef.current.scrollHeight, behavior: "smooth" });
            }
        }, 80);
    };

    useEffect(() => { if (showChatBox) scrollToBottom(); }, [messages, showChatBox]);

    const sendMessage = async () => {
        const text = input.trim();
        if (!text || loading) return;

        const history = messages
            .filter((m) => m.content)
            .map((m) => ({ role: m.role === "ai" ? "model" : "user", text: m.content }));

        setMessages((prev) => [...prev, { role: "user", content: text, movies: [] }]);
        setInput("");
        setLoading(true);

        try {
            const res = await fetch(AI_API, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ message: text, history }),
            });
            const data = await res.json();
            if (data?.success) {
                setMessages((prev) => [...prev, { role: "ai", content: data.reply || "", movies: data.movies || [] }]);
            } else {
                setMessages((prev) => [...prev, { role: "ai", content: data?.message || "Xin lỗi, mình chưa trả lời được. Bạn thử lại nhé.", movies: [] }]);
            }
        } catch (e) {
            setMessages((prev) => [...prev, { role: "ai", content: "Mất kết nối tới máy chủ. Bạn kiểm tra mạng rồi thử lại nhé.", movies: [] }]);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={`chat-box-container ${showChatBox ? "show" : ""}`}>
            {showChatBox && (
                <div className="chat-box">
                    <div className="chat-header">
                        <span>🎬 Trợ lý phim AI</span>
                        <button onClick={toggleChatBox} className="close-button">
                            <CloseOutlined />
                        </button>
                    </div>

                    {!isAuthenticated ? (
                        <div className="chat-content" style={{ display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: "8px", color: "#888" }}>
                            <span style={{ fontSize: "32px" }}>🔒</span>
                            <p style={{ margin: 0, textAlign: "center" }}>Vui lòng <strong>đăng nhập</strong> để chat với trợ lý phim AI.</p>
                        </div>
                    ) : (
                        <div className="chat-content" ref={contentRef}>
                            {messages.map((msg, i) => (
                                <div key={i} className={`message-container ${msg.role === "user" ? "sent" : "received"}`}>
                                    <div className="message-bubble">
                                        <div className="message-meta">
                                            <span className="sender-name">{msg.role === "user" ? "Bạn" : "Trợ lý AI"}</span>
                                        </div>
                                        {msg.content && <div className="message-text">{msg.content}</div>}
                                        {msg.movies && msg.movies.length > 0 && (
                                            <div className="ai-movie-grid">
                                                {msg.movies.map((m) => <MovieResult key={m.id} m={m} />)}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                            {loading && (
                                <div className="message-container received">
                                    <div className="message-bubble">
                                        <div className="message-text ai-typing">Đang tìm phim cho bạn<span>.</span><span>.</span><span>.</span></div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    <div className="chat-input">
                        <input
                            type="text"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") sendMessage(); }}
                            placeholder={isAuthenticated ? "Mô tả phim bạn muốn xem..." : "Đăng nhập để chat..."}
                            disabled={!isAuthenticated || loading}
                        />
                        <button onClick={sendMessage} disabled={!input.trim() || !isAuthenticated || loading}>
                            {loading ? "..." : "Gửi"}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ChatBox;
